import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { fmt } from './calculo';
import type { Criterios } from './types';

export interface LinhaOrigem {
  id: string;
  nome: string;
  secao: string | null;
  peso: number | null;
  nota: number | null;
  status: string | null;
  justificativa: string | null;
}

/**
 * "De onde veio a nota do grupo": cada item com o peso, o que o grupo ganhou, o que ficou de fora e o porquê.
 * O mesmo componente aparece para o aluno e na tela de aprovação do professor.
 */
export function TabelaOrigem({
  linhas,
  foraDoPrazo = false,
}: {
  linhas: LinhaOrigem[];
  foraDoPrazo?: boolean;
}) {
  const totalPeso = linhas.reduce((s, l) => s + (l.peso ?? 0), 0);
  const totalNota = linhas.reduce((s, l) => s + (l.nota ?? 0), 0);
  return (
    <TableContainer>
      <Table size="small" sx={{ '& td, & th': { verticalAlign: 'top' } }}>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Vale</TableCell>
            <TableCell align="right">Ganhou</TableCell>
            <TableCell align="right">Ficou de fora</TableCell>
            <TableCell>Por quê</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {linhas.map((l) => {
            const perdeu = l.peso !== null && l.nota !== null ? Math.max(0, l.peso - l.nota) : null;
            return (
              <TableRow key={l.id}>
                <TableCell sx={{ minWidth: 160 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {l.nome}
                  </Typography>
                  {l.secao && (
                    <Typography variant="caption" color="text.secondary">
                      {l.secao}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{fmt(l.peso)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmt(l.nota)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: perdeu ? 'warning.main' : 'text.secondary',
                    fontWeight: perdeu ? 700 : 400,
                  }}
                >
                  {perdeu ? `−${fmt(perdeu)}` : '–'}
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        l.status === 'Sim' ? 'success' : l.status === 'Não' ? 'error' : 'warning'
                      }
                      label={l.status ?? '–'}
                      sx={{ flexShrink: 0 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {l.justificativa ?? 'Sem comentários.'}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Total do grupo</TableCell>
            <TableCell align="right" sx={{ color: 'text.primary' }}>
              {fmt(totalPeso)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {fmt(totalNota)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: 'warning.main' }}>
              −{fmt(Math.max(0, totalPeso - totalNota))}
            </TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>
              {foraDoPrazo
                ? 'Entrega fora do prazo: a nota do grupo registrada é 0; esta correção é só referência.'
                : ''}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

/** Critérios públicos da correção de uma disciplina (versão revisada pelo professor). */
export function CriteriosView({ c, expandido = false }: { c: Criterios; expandido?: boolean }) {
  return (
    <Stack spacing={1}>
      <Box>
        <Typography variant="subtitle2">Prova: {c.prova}</Typography>
        <Typography variant="body2" color="text.secondary">
          {c.entrega}
        </Typography>
      </Box>
      <Bloco titulo="Como foi corrigido" aberto={expandido}>
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
          {c.como.map((t) => (
            <Typography component="li" variant="body2" key={t}>
              {t}
            </Typography>
          ))}
        </Stack>
      </Bloco>
      <Bloco titulo="Quanto vale cada parte (total 10)" aberto={expandido}>
        <Table size="small">
          <TableBody>
            {c.pesos.map((p) => (
              <TableRow key={p.grupo + p.itens}>
                <TableCell sx={{ fontWeight: 600 }}>{p.grupo}</TableCell>
                <TableCell>{p.itens}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {fmt(p.peso)}
                  {p.cada ? ' cada' : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Bloco>
      <Bloco titulo="O que não foi descontado" aberto={expandido}>
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
          {c.naoDescontado.map((t) => (
            <Typography component="li" variant="body2" key={t}>
              {t}
            </Typography>
          ))}
        </Stack>
      </Bloco>
      <Bloco
        titulo={`Descontos aplicados igualmente a todos os grupos (${c.descontos.length})`}
        aberto={expandido}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Situação</TableCell>
              <TableCell>Efeito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {c.descontos.map((d) => (
              <TableRow key={d.situacao}>
                <TableCell>{d.situacao}</TableCell>
                <TableCell sx={{ whiteSpace: { sm: 'nowrap' } }}>{d.efeito}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {c.prazo && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {c.prazo}
          </Typography>
        )}
      </Bloco>
    </Stack>
  );
}

function Bloco({
  titulo,
  aberto,
  children,
}: {
  titulo: string;
  aberto: boolean;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={aberto}
      variant="outlined"
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Typography variant="subtitle2">{titulo}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}
